import React, { useContext, useRef } from 'react';
import type { ComponentProps } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { markErrorAsHandled } from '@confluence/graphql';
import { CoreInvitesContext } from '@confluence/core-invites-provider';
import type { CoreInvitesSource } from '@confluence/core-invites-provider';
import {
	Attribution,
	ErrorDisplay,
	isUnauthorizedError,
	TransparentErrorBoundary,
} from '@confluence/error-boundary';
import { useElementSeenCallback } from '@confluence/analytics';

import {
	InviteButtonComponent,
	IconButtonComponent,
} from './PersistentInvitePeopleButtonComponent';
import type { Source } from './PersistentInvitePeopleButtonComponent';
import { usePersistentInviteButtonEligible } from './usePersistentInviteButtonEligible';

const PersistentInvitePeopleButtonComponent = ({
	source,
	hasIconButton,
}: {
	source: Source;
	hasIconButton?: boolean;
}) => {
	const ref = useRef(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { openCoreInvites } = useContext(CoreInvitesContext);

	const {
		isEligible: isPersistentInviteButtonEligible,
		loading: persistentInviteButtonEligibleLoading,
		error: persistentInviteButtonEligibleError,
	} = usePersistentInviteButtonEligible();

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		event.stopPropagation();

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'persistentInvite',
				source,
			},
		}).fire();

		openCoreInvites?.(source as CoreInvitesSource, {
			onModalClosed: () => null,
			onInviteCompleted: () => null,
		});
	};

	const handleElementSeen = () => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'persistentInvitePeopleButton',
				attributes: {
					source,
				},
			},
		}).fire();
	};
	useElementSeenCallback(ref, handleElementSeen);

	if (persistentInviteButtonEligibleLoading) return null;

	if (persistentInviteButtonEligibleError) {
		if (isUnauthorizedError(persistentInviteButtonEligibleError)) {
			markErrorAsHandled(persistentInviteButtonEligibleError);
		} else {
			return <ErrorDisplay error={persistentInviteButtonEligibleError} />;
		}
	}

	return isPersistentInviteButtonEligible && openCoreInvites ? (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		<div ref={ref} className="persistent-invite-button-container">
			{hasIconButton && <IconButtonComponent onClick={handleClick} />}
			<InviteButtonComponent onClick={handleClick} />
		</div>
	) : null;
};

export const PersistentInvitePeopleButton = (
	props: ComponentProps<typeof PersistentInvitePeopleButtonComponent>,
) => (
	<TransparentErrorBoundary attribution={Attribution.CC_ONBOARDING}>
		<PersistentInvitePeopleButtonComponent {...props} />
	</TransparentErrorBoundary>
);
